'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import { Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Command, CommandInputRaw, CommandList } from '@kit/ui/command';
import { cn } from '@kit/ui/utils';

import { useBrand } from '~/components/brand-provider';
import {
  isCrossBrand,
  resolveCrossBrandHref,
} from '~/lib/routing/resolve-app-url';
import { useGlobalSearchOptional } from './global-search-provider';
import { GlobalSearchResults } from './global-search-results';
import type { GlobalSearchKind } from './global-search-service';
import { GlobalSearchTrigger } from './global-search-trigger';
import { useGlobalSearchQuery } from './use-global-search';

/**
 * Inline global search for the app top bar (2026-07-05, replaces the modal
 * overlay on this surface; extended 2026-07-13 to the logged-out marketing
 * `SiteHeader` so anon and authed visitors share ONE centered search bar
 * instead of the header opening a separate modal overlay).
 *
 * Provider guard: `GlobalSearchInlineLive` calls `useGlobalSearchQuery`, which
 * uses `@tanstack/react-query`'s `useQuery` — that throws without a
 * `ReactQueryProvider` ancestor. The bare root `not-found.tsx` can render the
 * marketing header OUTSIDE `RootProviders` (a POST to an action-less page
 * falls through to it; Sentry BALLEE-EZ crashed the old throwing trigger the
 * same way). `ReactQueryProvider` and `GlobalSearchProvider` are siblings in
 * `RootProviders`, so a null `useGlobalSearchOptional()` is a safe proxy for
 * "no query client" — degrade to the inert `GlobalSearchTrigger` bar there
 * rather than crash the 404.
 */
export function GlobalSearchInline({ className }: { className?: string }) {
  // Null context ⇒ RootProviders (incl. ReactQueryProvider) is absent — render
  // the provider-safe trigger instead of the query-driven live component.
  const providersPresent = useGlobalSearchOptional() !== null;

  if (!providersPresent) {
    return <GlobalSearchTrigger variant="bar" className={className} />;
  }

  return <GlobalSearchInlineLive className={className} />;
}

/**
 * The live inline search. The results panel GROWS OUT of the input (origin-top
 * scale/opacity/translate transition) instead of a centered dialog — the user
 * types straight into the bar and the panel expands beneath it. Reference:
 * Cosmos / Fey inline search (Mobbin).
 *
 * Still one `Command` so cmdk owns arrow-key + Enter navigation across the
 * results; `shouldFilter={false}` because rows are server-ranked. The panel
 * is absolutely positioned, so it overlays page content without shifting
 * layout. Closes on outside-click, Escape, or navigation.
 *
 * The Cmd+K dialog (GlobalSearchProvider) stays mounted for surfaces that
 * don't render this bar; here ⌘K focuses this input instead.
 */
function GlobalSearchInlineLive({ className }: { className?: string }) {
  const { t } = useTranslation('search');
  const router = useRouter();
  const brand = useBrand();
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);

  const { query, setQuery, debouncedQuery, enabled, page, isLoading } =
    useGlobalSearchQuery();

  const close = useCallback(() => {
    setOpen(false);
    inputRef.current?.blur();
  }, []);

  const navigate = useCallback(
    (href: string) => {
      close();
      setQuery('');
      if (isCrossBrand(href, brand)) {
        window.location.assign(resolveCrossBrandHref(href, brand));
        return;
      }
      router.push(href);
    },
    [brand, close, router, setQuery],
  );

  const seeAll = useCallback(
    (kind: GlobalSearchKind) => {
      navigate(`/search?q=${encodeURIComponent(debouncedQuery)}&type=${kind}`);
    },
    [debouncedQuery, navigate],
  );

  // ⌘K / Ctrl+K focuses this input (plain ⌘K; ⌘⇧K stays the super-admin
  // nav palette). preventDefault beats the browser's find-in-page bar.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && !e.shiftKey && e.key === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
        setOpen(true);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  // Outside-click closes the panel (but keeps the typed query, so a
  // re-focus reopens where the user left off).
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open]);

  return (
    <div
      ref={containerRef}
      data-global-search-inline=""
      className={cn('relative w-full', className)}
    >
      <Command
        shouldFilter={false}
        // overflow-visible so the absolutely-positioned panel isn't clipped
        // by cmdk's default rounded container.
        className="overflow-visible bg-transparent"
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            e.preventDefault();
            close();
          }
        }}
      >
        <div
          className={cn(
            'bg-muted/60 hover:bg-muted flex h-9 w-full items-center gap-2 rounded-md px-3 text-sm transition-colors',
            'focus-within:bg-muted focus-within:ring-ring focus-within:ring-1',
          )}
        >
          <Search className="text-muted-foreground size-4 shrink-0" />
          <CommandInputRaw
            ref={inputRef}
            value={query}
            onValueChange={(v) => {
              setQuery(v);
              if (!open) setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            placeholder={t('placeholder')}
            className="flex-1"
          />
        </div>

        {/* Results panel — grows out of the input. */}
        <div
          className={cn(
            'bg-popover text-popover-foreground absolute top-[calc(100%+0.5rem)] left-0 z-50 w-full origin-top overflow-hidden rounded-md shadow-lg',
            'transition-all duration-200 ease-out',
            open
              ? 'pointer-events-auto translate-y-0 scale-100 opacity-100'
              : 'pointer-events-none -translate-y-1 scale-[0.98] opacity-0',
          )}
        >
          <CommandList className="[&_[cmdk-group-heading]]:text-muted-foreground max-h-[min(24rem,60dvh)] p-1 [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group]]:px-1">
            <GlobalSearchResults
              query={debouncedQuery}
              enabled={enabled}
              isLoading={isLoading}
              page={page}
              onNavigate={navigate}
              onSeeAll={seeAll}
            />
          </CommandList>
        </div>
      </Command>
    </div>
  );
}
